import React, { useEffect, useMemo, useState } from 'react';
import { urlApiProd } from '../utiles';
import EditarSolicitud from '../solicitudesbooking/EditarSolicitud';
import GestionCPO from '../solicitudesbooking/GestionCPO';
import { ThOrden, ordenarFilas, useOrdenTabla } from './ordenTabla';

const POR_PAGINA = 15;
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Estados del booking (los mismos que usa /solicitudes) y la fila derivada "por solicitar",
// que sale de las solicitudes internas con HC aún sin solicitar.
const RESERVAS = {
    por_solicitar: { label: 'Por solicitar', clase: 'ce-pill--sin' },
    0: { label: 'Pendiente', clase: 'ce-pill--neutro' },
    1: { label: 'Solicitado', clase: 'ce-pill--solicitado' },
    2: { label: 'Confirmado', clase: 'ce-pill--confirmado' },
    3: { label: 'Embarcado', clase: 'ce-pill--embarcado' },
    '-1': { label: 'Cancelado', clase: 'ce-pill--neutro' },
    99: { label: 'Transferencia', clase: 'ce-pill--neutro' },
};

const CHIPS = ['por_solicitar', '0', '1', '2', '3', '-1', '99'];

const fmtDiaMes = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const mesDe = (iso) => (iso ? iso.slice(0, 7) : '');
const etiquetaMes = (anioMes) => {
    const [anio, mes] = anioMes.split('-');
    return `${MESES[Number(mes) - 1]} ${anio}`;
};

const hoyIso = () => new Date().toISOString().slice(0, 10);

// Orden: por solicitar primero, luego lo vigente por ETD, después embarcados y cancelados.
const grupoOrden = (fila) => {
    if (fila.estadoClave === 'por_solicitar') return 0;
    if (['0', '1', '2'].includes(fila.estadoClave)) return 1;
    if (fila.estadoClave === '3') return 2;
    return 3;
};

const asignacionDe = (fila) => {
    if (fila.tipo === 'interna') return { label: 'Pendiente', clase: 'ce-pill--neutro' };
    if (['-1', '99'].includes(fila.estadoClave)) return null;
    if (fila.asignados === 0) return { label: 'Sin asignar', clase: 'ce-pill--sin' };
    if (fila.asignados < fila.capacidad) return { label: 'Parcial', clase: 'ce-pill--solicitado' };
    return { label: 'Total', clase: 'ce-pill--embarcado' };
};

const accionDe = (fila) => {
    if (fila.tipo === 'interna') return 'Solicitar booking';
    if (fila.estadoClave === '1') return 'Confirmar';
    if (fila.estadoClave === '2' && fila.asignados < fila.capacidad) return 'Asignar';
    return 'Abrir ficha';
};

// Valores por los que se ordena cada columna (Reserva por su orden lógico; Acción no se ordena)
const ACCESORES = {
    booking: (f) => f.nroBooking,
    solicitud: (f) => f.solicitud,
    fw: (f) => [f.ffw, f.carrier].filter(Boolean).join(' '),
    ruta: (f) => [f.pol, f.pod].filter(Boolean).join(' → '),
    nave: (f) => f.nave,
    etd: (f) => f.etd,
    dld: (f) => f.dld,
    dlc: (f) => f.dlc,
    hc: (f) => f.asignados,
    reserva: (f) => CHIPS.indexOf(f.estadoClave),
    asignacion: (f) => asignacionDe(f)?.label,
};

const Bookings = ({ version = 0, onSolicitarBooking }) => {
    const [datos, setDatos] = useState({ solicitudes: [], internas: [], puertos: [], ffw: [], carriers: [], clientes: [] });
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [recarga, setRecarga] = useState(0);
    const [filtros, setFiltros] = useState({ texto: '', origen: '', destino: '', ffw: '', carrier: '', mes: '' });
    const [chip, setChip] = useState('');
    const [pagina, setPagina] = useState(1);
    const [editando, setEditando] = useState(null);
    const [enFicha, setEnFicha] = useState(null);
    const [orden, alternarOrden] = useOrdenTabla();

    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            setCargando(true);
            const token = localStorage.getItem('token');
            try {
                const rutas = ['solicitudesbooking', 'solicitudesinternas', 'puertos', 'ffw', 'carriers', 'clientes'];
                const respuestas = await Promise.all(
                    rutas.map((ruta) => fetch(`${urlApiProd}/${ruta}`, { headers: { Authorization: `Bearer ${token}` } }))
                );
                const [solicitudes, internas, puertos, ffw, carriers, clientes] = await Promise.all(
                    respuestas.map((res) => {
                        if (!res.ok) throw new Error('No se pudieron cargar los bookings');
                        return res.json();
                    })
                );
                puertos.sort((a, b) => a.nombre.localeCompare(b.nombre));
                if (activo) {
                    setDatos({ solicitudes, internas, puertos, ffw, carriers, clientes });
                    setError('');
                }
            } catch (e) {
                if (activo) setError(e.message);
            } finally {
                if (activo) setCargando(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, [version, recarga]);

    // Solicitudes internas con HC sin solicitar: son las filas "por solicitar" y las que ofrecen los diálogos
    const internasPendientes = useMemo(
        () => datos.internas.filter(
            (s) => (s.estado === 0 || s.estado === 1) && s.cantidad - (s.cantidad_solicitada || 0) > 0
        ),
        [datos.internas]
    );

    const filas = useMemo(() => {
        const nombre = (lista, clave, id) => lista.find((x) => Number(x[clave]) === Number(id))?.nombre || null;

        const bookings = datos.solicitudes.map((b) => {
            const estado = Number(b.estado) || 0;
            const interna = b.solicitud_interna;
            return {
                tipo: 'booking',
                id: `b-${b.id_solicitud}`,
                estado,
                estadoClave: String(estado),
                booking: b,
                nroBooking: (b.nro_booking || '').trim(),
                solicitud: interna ? interna.codigo : null,
                cliente: interna ? nombre(datos.clientes, 'id_cliente', interna.id_cliente) : null,
                ffw: b.id_ffw ? nombre(datos.ffw, 'id_ffw', b.id_ffw) : null,
                carrier: nombre(datos.carriers, 'id_carrier', b.id_carrier),
                idOrigen: b.id_puerto_origen,
                idDestino: b.id_puerto_destino,
                idFfw: b.id_ffw,
                idCarrier: b.id_carrier,
                pol: nombre(datos.puertos, 'id_puerto', b.id_puerto_origen),
                pod: nombre(datos.puertos, 'id_puerto', b.id_puerto_destino),
                nave: b.nave,
                etd: b.etd,
                dld: b.dld,
                dlc: b.dlc,
                asignados: Number(b.cantidad_asignada) || 0,
                capacidad: Number(b.cantidad) || 0,
            };
        });

        const porSolicitar = internasPendientes.map((s) => {
            const pendiente = s.cantidad - (s.cantidad_solicitada || 0);
            return {
                tipo: 'interna',
                id: `i-${s.id_solicitudinterna}`,
                estado: null,
                estadoClave: 'por_solicitar',
                interna: s,
                nroBooking: '',
                solicitud: s.codigo,
                cliente: nombre(datos.clientes, 'id_cliente', s.id_cliente),
                subtexto: pendiente < s.cantidad ? `${pendiente} HC rest.` : `${s.cantidad} HC`,
                ffw: null,
                carrier: null,
                idOrigen: null,
                idDestino: s.id_puerto_destino,
                idFfw: null,
                idCarrier: null,
                pol: null,
                pod: nombre(datos.puertos, 'id_puerto', s.id_puerto_destino),
                nave: null,
                etd: s.etd,
                dld: null,
                dlc: null,
                asignados: 0,
                capacidad: pendiente,
            };
        });

        return [...porSolicitar, ...bookings];
    }, [datos, internasPendientes]);

    const meses = useMemo(
        () => [...new Set(filas.map((f) => mesDe(f.etd)).filter(Boolean))].sort(),
        [filas]
    );

    // Filtros comunes; los chips de estado cuentan sobre este conjunto
    const filtradas = useMemo(() => {
        const q = filtros.texto.trim().toLowerCase();
        return filas.filter((f) => {
            if (filtros.origen && Number(f.idOrigen) !== Number(filtros.origen)) return false;
            if (filtros.destino && Number(f.idDestino) !== Number(filtros.destino)) return false;
            if (filtros.ffw && Number(f.idFfw) !== Number(filtros.ffw)) return false;
            if (filtros.carrier && Number(f.idCarrier) !== Number(filtros.carrier)) return false;
            if (filtros.mes && mesDe(f.etd) !== filtros.mes) return false;
            if (!q) return true;
            const texto = [f.nroBooking, f.solicitud, f.cliente, f.nave].filter(Boolean).join(' ').toLowerCase();
            return texto.includes(q);
        });
    }, [filas, filtros]);

    const conteos = useMemo(() => {
        const base = { todos: filtradas.length };
        CHIPS.forEach((c) => { base[c] = filtradas.filter((f) => f.estadoClave === c).length; });
        return base;
    }, [filtradas]);

    const visiblesTodas = useMemo(() => {
        const lista = chip ? filtradas.filter((f) => f.estadoClave === chip) : filtradas;
        // Con una columna elegida manda ese orden; sin ella, el orden operativo por defecto
        if (orden.clave) return ordenarFilas(lista, orden, ACCESORES);
        return lista.slice().sort((a, b) => {
            const grupo = grupoOrden(a) - grupoOrden(b);
            if (grupo !== 0) return grupo;
            const fa = a.etd || '';
            const fb = b.etd || '';
            if (fa === fb) return 0;
            // lo vigente por ETD ascendente; embarcados y cancelados, los más recientes primero
            const asc = grupoOrden(a) <= 1;
            return asc ? fa.localeCompare(fb) : fb.localeCompare(fa);
        });
    }, [filtradas, chip, orden]);

    useEffect(() => {
        setPagina(1);
    }, [filtros, chip, orden]);

    const totalPaginas = Math.max(1, Math.ceil(visiblesTodas.length / POR_PAGINA));
    const paginaActual = Math.min(pagina, totalPaginas);
    const desde = (paginaActual - 1) * POR_PAGINA;
    const visibles = visiblesTodas.slice(desde, desde + POR_PAGINA);

    const actualizarFiltro = (campo) => (e) => setFiltros((actual) => ({ ...actual, [campo]: e.target.value }));
    const hoy = hoyIso();

    // Misma regla que /solicitudes: cancelado, pendiente y solicitado se editan; el resto va a la ficha de gestión
    const abrir = (fila) => {
        if (fila.tipo === 'interna') {
            if (onSolicitarBooking) onSolicitarBooking();
        } else if ([-1, 0, 1].includes(fila.estado)) {
            setEditando(fila.booking);
        } else {
            setEnFicha(fila.booking);
        }
    };

    const fechaLimite = (iso, fila) => {
        const vencida = iso && iso < hoy && ['0', '1', '2'].includes(fila.estadoClave);
        return <span className={`ce-mono${vencida ? ' ce-fecha-vencida' : ''}`}>{fmtDiaMes(iso)}</span>;
    };

    return (
        <>
            <div className="ce-filtros">
                <label className="ce-filtro ce-filtro-buscar">
                    Buscar booking, solicitud o cliente
                    <input
                        type="text"
                        placeholder="Ej: 45464748, KOL2026…"
                        value={filtros.texto}
                        onChange={actualizarFiltro('texto')}
                    />
                </label>
                <label className="ce-filtro">
                    Puerto origen
                    <select value={filtros.origen} onChange={actualizarFiltro('origen')}>
                        <option value="">Todos</option>
                        {datos.puertos.map((p) => <option key={p.id_puerto} value={p.id_puerto}>{p.nombre}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Puerto destino
                    <select value={filtros.destino} onChange={actualizarFiltro('destino')}>
                        <option value="">Todos</option>
                        {datos.puertos.map((p) => <option key={p.id_puerto} value={p.id_puerto}>{p.nombre}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Forwarder
                    <select value={filtros.ffw} onChange={actualizarFiltro('ffw')}>
                        <option value="">Todos</option>
                        {datos.ffw.map((f) => <option key={f.id_ffw} value={f.id_ffw}>{f.nombre}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Carrier
                    <select value={filtros.carrier} onChange={actualizarFiltro('carrier')}>
                        <option value="">Todos</option>
                        {datos.carriers.map((c) => <option key={c.id_carrier} value={c.id_carrier}>{c.nombre}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Mes ETD
                    <select value={filtros.mes} onChange={actualizarFiltro('mes')}>
                        <option value="">Todos</option>
                        {meses.map((m) => <option key={m} value={m}>{etiquetaMes(m)}</option>)}
                    </select>
                </label>
            </div>

            <div className="ce-chips">
                <button type="button" className={`ce-chip${chip === '' ? ' active' : ''}`} onClick={() => setChip('')}>
                    Todos {conteos.todos}
                </button>
                {CHIPS.filter((c) => conteos[c] > 0 || ['por_solicitar', '1', '2', '3', '-1'].includes(c)).map((c) => (
                    <button
                        key={c}
                        type="button"
                        className={`ce-chip${chip === c ? ' active' : ''}`}
                        onClick={() => setChip(chip === c ? '' : c)}
                    >
                        {RESERVAS[c].label} {conteos[c]}
                    </button>
                ))}
            </div>

            {cargando && <div className="ce-muted py-3">Cargando bookings…</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {!cargando && !error && (
                <div className="table-responsive">
                    <table className="ce-tabla">
                        <thead>
                            <tr>
                                <ThOrden clave="booking" orden={orden} alternarOrden={alternarOrden}>Booking</ThOrden>
                                <ThOrden clave="solicitud" orden={orden} alternarOrden={alternarOrden}>Solicitud · Cliente</ThOrden>
                                <ThOrden clave="fw" orden={orden} alternarOrden={alternarOrden}>FW · Carrier</ThOrden>
                                <ThOrden clave="ruta" orden={orden} alternarOrden={alternarOrden}>POL → POD</ThOrden>
                                <ThOrden clave="nave" orden={orden} alternarOrden={alternarOrden}>Nave</ThOrden>
                                <ThOrden clave="etd" orden={orden} alternarOrden={alternarOrden}>ETD</ThOrden>
                                <ThOrden clave="dld" orden={orden} alternarOrden={alternarOrden}>DLD</ThOrden>
                                <ThOrden clave="dlc" orden={orden} alternarOrden={alternarOrden}>DLC</ThOrden>
                                <ThOrden clave="hc" orden={orden} alternarOrden={alternarOrden}>HC asig./cap.</ThOrden>
                                <ThOrden clave="reserva" orden={orden} alternarOrden={alternarOrden}>Reserva</ThOrden>
                                <ThOrden clave="asignacion" orden={orden} alternarOrden={alternarOrden}>Asignación</ThOrden>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((fila) => {
                                const reserva = RESERVAS[fila.estadoClave] || { label: 'Sin estado', clase: 'ce-pill--neutro' };
                                const asignacion = asignacionDe(fila);
                                const excede = fila.tipo === 'booking' && fila.asignados > fila.capacidad;
                                return (
                                    <tr key={fila.id}>
                                        <td className="ce-mono">
                                            {fila.nroBooking || (fila.tipo === 'interna' ? '—' : `Sin N° (${fila.ffw || '—'})`)}
                                        </td>
                                        <td>
                                            <span className="ce-mono">{fila.solicitud || 'Sin solicitud interna'}</span>
                                            <div className="ce-muted">
                                                {[fila.cliente, fila.subtexto].filter(Boolean).join(' · ') || '—'}
                                            </div>
                                        </td>
                                        <td>{[fila.ffw, fila.carrier].filter(Boolean).join(' · ') || '—'}</td>
                                        <td>{fila.pol || '—'} → {fila.pod || '—'}</td>
                                        <td>{fila.nave || (fila.estadoClave === '1' ? 'Por confirmar' : '—')}</td>
                                        <td className="ce-mono">{fmtDiaMes(fila.etd)}</td>
                                        <td>{fechaLimite(fila.dld, fila)}</td>
                                        <td>{fechaLimite(fila.dlc, fila)}</td>
                                        <td className="ce-mono" style={{ fontWeight: 700, color: excede ? '#a3322a' : undefined }}>
                                            {fila.asignados} / {fila.capacidad}
                                        </td>
                                        <td><span className={`ce-pill ${reserva.clase}`}>{reserva.label}</span></td>
                                        <td>
                                            {asignacion ? <span className={`ce-pill ${asignacion.clase}`}>{asignacion.label}</span> : '—'}
                                        </td>
                                        <td>
                                            <button type="button" className="ce-accion" onClick={() => abrir(fila)}>
                                                {accionDe(fila)}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {visibles.length === 0 && (
                                <tr>
                                    <td colSpan={12} className="ce-muted text-center py-4">
                                        No hay bookings que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {!cargando && !error && visiblesTodas.length > 0 && (
                <div className="ce-paginacion">
                    <span className="ce-muted">
                        {desde + 1}–{desde + visibles.length} de {visiblesTodas.length}
                    </span>
                    <div className="ce-paginacion-botones">
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === 1}
                            onClick={() => setPagina(paginaActual - 1)}
                        >
                            Anterior
                        </button>
                        <span className="ce-muted">Página {paginaActual} de {totalPaginas}</span>
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === totalPaginas}
                            onClick={() => setPagina(paginaActual + 1)}
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}

            <div className="ce-nota">
                Muestra todos los bookings y, como filas "Por solicitar", las solicitudes internas con HC aún sin solicitar.
                Cancelado, pendiente y solicitado se editan; confirmado y embarcado abren la ficha de gestión.
                Un booking no acepta más HC asignados que su capacidad.
            </div>

            {editando && (
                <EditarSolicitud
                    solicitud={editando}
                    puertos={datos.puertos}
                    ffw={datos.ffw}
                    carriers={datos.carriers}
                    solicitudesInternas={internasPendientes}
                    cerrar={() => { setEditando(null); setRecarga((n) => n + 1); }}
                />
            )}
            {enFicha && (
                <GestionCPO
                    desde={'bookingexterno'}
                    solicitud={enFicha}
                    puertos={datos.puertos}
                    ffw={datos.ffw}
                    carriers={datos.carriers}
                    solicitudesInternas={internasPendientes}
                    cerrar={() => { setEnFicha(null); setRecarga((n) => n + 1); }}
                />
            )}
        </>
    );
};

export default Bookings;
